import {
  type HydratedGeneration,
  HydratedGenerationSchema
} from '@stratumn/studio-shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { generationKeys } from '@/lib/query-keys';
import { TooManyRequestsError } from '@/services/errors/studio-errors';
import { HTTPWrapper } from '@/utils/http';

const http = new HTTPWrapper<unknown>('process');

type CreateGenerationInput = {
  workspaceId?: string;
};

const createGeneration = async (
  processId: string,
  input: CreateGenerationInput
): Promise<HydratedGeneration> => {
  const queryParams = input.workspaceId
    ? `?workspaceId=${encodeURIComponent(input.workspaceId)}`
    : '';

  try {
    const response = await http.get<unknown>(
      `/${processId}/generate${queryParams}`
    );

    return HydratedGenerationSchema.parse(response);
  } catch (err) {
    // Restore the explanatory rate-limit message that the previous
    // MobX-based implementation surfaced; the bare `TooManyRequestsError`
    // ships with no message, which led to "Generation failed: " toasts.
    if (err instanceof TooManyRequestsError) {
      throw new TooManyRequestsError(
        'You can only trigger a new generation 3 times per minute'
      );
    }
    throw err;
  }
};

type UseCreateGenerationOptions = {
  processId: string;
};

export const useCreateGeneration = ({
  processId
}: UseCreateGenerationOptions) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (input: CreateGenerationInput) =>
      createGeneration(processId, input),
    onSuccess: _response => {
      // Narrow the invalidation: only lists/counts change when a new
      // generation is created. Detail/log caches for unrelated generations
      // should not be wiped — that just causes a refetch storm on dashboards.
      queryClient.invalidateQueries({
        queryKey: generationKeys.lists(processId)
      });
      queryClient.invalidateQueries({
        queryKey: generationKeys.counts(processId)
      });

      // The mutation response uses the legacy snake_case `HydratedGeneration`
      // shape, while detail queries expect the camelCase `GenerationDetail`
      // shape. We can't pre-seed the detail cache without a transformer,
      // so we leave the navigation target to fetch fresh.
    }
  });

  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error
  };
};
